import { Card, Container, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import {
    SaveButton,
    SimpleForm,
    Toolbar,
    useDataProvider,
    useEditController,
    useNotify,
    useRecordContext,
    useRedirect,
    required,
    TextInput,
    SelectInput,
    DateInput,
} from 'react-admin';
import { SubmitHandler } from 'react-hook-form';
import { Fund } from '../../types/types';
import { IrmDataProvider } from '../../providers/types';

function EditToolbar() {
    return (
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <SaveButton />
        </Toolbar>
    );
}

export function FundEdit() {
    const { record } = useEditController<Fund>();
    const dataProvider = useDataProvider<IrmDataProvider>();
    const notify = useNotify();
    const redirect = useRedirect();

    const { mutate } = useMutation({
        mutationKey: ['funds-update'],
        mutationFn: async (data: Fund) => {
            if (!record) throw new Error('Record not found');
            return dataProvider.updateFund(record.id, data);
        },
        onSuccess: () => {
            notify('Fund updated successfully');
            redirect(`/funds/${record?.id}/show`);
        },
        onError: () => {
            notify('An error occurred. Please try again.', { type: 'error' });
        },
    });

    const onSubmit: SubmitHandler<Fund> = async (data) => {
        mutate(data);
    };

    return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
            <Card>
                <SimpleForm
                    toolbar={<EditToolbar />}
                    onSubmit={onSubmit as SubmitHandler<any>}
                    record={record}
                >
                    <FundEditTitle />
                    <FundInputs />
                </SimpleForm>
            </Card>
        </Container>
    );
}

const FundEditTitle = () => {
    const record = useRecordContext<Fund>();
    if (!record) return null;
    return (
        <Typography variant="h6" sx={{ mb: 2 }}>
            Edit {record.name}
        </Typography>
    );
};

export function FundInputs() {
    const record = useRecordContext<Fund>();
    const isEditing = Boolean(record?.id);

    return (
        <Stack gap={1} sx={{ width: '100%' }}>
            {/* General Info */}
            <TextInput source="name" validate={required()} helperText={false} />
            <TextInput source="fund_type" validate={required()} helperText={false} />
            <TextInput source="manager" validate={required()} helperText={false} />
            <TextInput source="corporation" validate={required()} helperText={false} />

            {/* Status */}
            <SelectInput
                source="status"
                choices={[
                    { id: 'open', name: 'Open' },
                    { id: 'closed', name: 'Closed' },
                ]}
                validate={required()}
                helperText={false}
            />

            {/* Description (long-form markdown) */}
            <TextInput
                source="description"
                multiline
                minRows={6}
                fullWidth
                helperText={false}
            />

            {/* Contact Info */}
            <TextInput source="website" helperText={false} />
            <TextInput source="email" validate={required()} helperText={false} />
            <TextInput source="country" validate={required()} helperText={false} />
            <TextInput source="address" helperText={false} />
            <TextInput source="zipcode" helperText={false} />
            <TextInput source="city" helperText={false} />

            {/* Bank Info 
            <TextInput source="bank" helperText={false} />
            <TextInput source="bank_account" helperText={false} />
            <TextInput source="iban" helperText={false} />
            <TextInput source="bic" helperText={false} />
            */}
        </Stack>
    );
}
